/**
 * Badge unifié utilisé partout où le panier liste un type d'élément.
 * Garantit une cohérence stricte icône + couleur entre :
 *   - CartDrawer (lignes panier + récap totaux)
 *   - CheckoutRedirect (récap totaux)
 *   - Page /merci, futurs récaps
 *
 * Trois variantes :
 *   - "sticker"  → 🏷️ Tag, terracotta (primary)
 *   - "donation" → ❤️ Heart, rose (Don Solidaire)
 *   - "tip"      → ☕ Coffee, ambre (Pourboire boutique)
 */
import { Tag, Heart, Coffee, type LucideIcon } from "lucide-react";

export type CartBadgeVariant = "sticker" | "donation" | "tip";
export type CartBadgeSize = "sm" | "md";

interface CartItemBadgeProps {
  variant: CartBadgeVariant;
  /** sm = inline (totaux), md = badge pill au-dessus du titre */
  size?: CartBadgeSize;
  /** Affiche la traduction arabe à droite du libellé FR. Default: true. */
  withArabic?: boolean;
  className?: string;
}

interface VariantConfig {
  Icon: LucideIcon;
  labelFr: string;
  labelAr: string;
  // Classes Tailwind pour la pill (size="md")
  pillBg: string;
  pillText: string;
  // Classes pour le mode inline (size="sm")
  inlineText: string;
  iconColor: string;
  ariaLabel: string;
}

const VARIANTS: Record<CartBadgeVariant, VariantConfig> = {
  sticker: {
    Icon: Tag,
    labelFr: "Adhésif",
    labelAr: "ملصق",
    pillBg: "bg-primary",
    pillText: "text-primary-foreground",
    inlineText: "text-primary",
    iconColor: "text-primary",
    ariaLabel: "Adhésif",
  },
  donation: {
    Icon: Heart,
    labelFr: "Don Solidaire",
    labelAr: "تبرع تضامني",
    pillBg: "bg-rose-600",
    pillText: "text-white",
    inlineText: "text-rose-700",
    iconColor: "text-rose-600",
    ariaLabel: "Don Solidaire pour les familles tunisiennes",
  },
  tip: {
    Icon: Coffee,
    labelFr: "Pourboire",
    labelAr: "إكرامية",
    pillBg: "bg-amber-700",
    pillText: "text-white",
    inlineText: "text-amber-800",
    iconColor: "text-amber-700",
    ariaLabel: "Pourboire boutique",
  },
};

export const CartItemBadge = ({
  variant,
  size = "md",
  withArabic = true,
  className = "",
}: CartItemBadgeProps) => {
  const config = VARIANTS[variant];
  const { Icon } = config;

  if (size === "sm") {
    return (
      <span
        className={`inline-flex items-center gap-1 font-body ${config.inlineText} ${className}`}
        aria-label={config.ariaLabel}
        dir="ltr"
      >
        <Icon className={`w-3 h-3 ${config.iconColor}`} aria-hidden="true" />
        <span>{config.labelFr}</span>
        {withArabic && (
          <span dir="rtl" lang="ar" className="text-[10px] opacity-90">
            · {config.labelAr}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] sm:text-[10px] font-display font-bold uppercase tracking-wide shadow-sm ${config.pillBg} ${config.pillText} ${className}`}
      aria-label={config.ariaLabel}
      dir="ltr"
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      <span>{config.labelFr}</span>
      {withArabic && (
        <span dir="rtl" lang="ar" className="font-body normal-case tracking-normal opacity-90">
          · {config.labelAr}
        </span>
      )}
    </span>
  );
};
